import type { Row } from "./adapter.js";
import type { MemoryPersistence, PersistenceDelta, Snapshot } from "./memory.js";

/** The base snapshot: one key holding every table. Same layout as v1. */
const BASE = "snapshot";
const BASE_KEY = "data";
/** Row-level changes made since the base was written, in order. */
const JOURNAL = "journal";
const DB_VERSION = 2;
/**
 * Journal records tolerated before the base is rewritten. Each one costs a
 * little at load; the rewrite costs a full clone. 200 keeps a normal session
 * (a few hundred edits) on the cheap path while bounding load to a few ms of
 * replay.
 */
const JOURNAL_MAX = 200;
/** …and a row budget, so one bulk sync does not park 10k rows in the journal. */
const JOURNAL_MAX_ROWS = 2000;

export interface JournalRecord {
  table: string;
  upserts: Row[];
  deletes: string[];
  cleared: boolean;
}

/**
 * Persists a {@link MemoryAdapter} snapshot to IndexedDB so browser data
 * survives a page reload. Uses the raw IndexedDB API to avoid a dependency.
 * No-ops where IndexedDB is absent (SSR, tests), leaving the adapter purely
 * in-memory.
 *
 * BASE + JOURNAL, because the two obvious layouts each lose one side:
 *
 * - One key for the whole snapshot (the original) reads in ~40 ms but makes
 *   every debounced save structured-clone every row on the main thread — ~55 ms
 *   about 150 ms after EVERY edit on a 12.5k-task dataset. Splitting it per
 *   table would not have helped: ~45 ms of that is the tasks table alone, and
 *   virtually every edit dirties `tasks`.
 * - One record per row makes writes free but turns the boot read into
 *   `getAllKeys` + `getAll` over ~20k records: measured 200 ms against 40 ms,
 *   i.e. paying 160 ms on every cold start to save 45 ms per edit. Bad trade for
 *   an app you open once and then use.
 *
 * So: the base keeps the single fast read, and each save appends only what
 * changed. Load replays the journal over the base; once it grows past
 * {@link JOURNAL_MAX} the base is rewritten and the journal cleared. Desktop
 * (Tauri SQLite) and Android (native SQLite) never paid any of this; the browser
 * and the PWA did.
 *
 * A v1 database needs no migration — its single key IS the base, with an empty
 * journal.
 */
export class IndexedDbPersistence implements MemoryPersistence {
  /** Journal records currently on disk; drives compaction. */
  private journalCount = 0;
  private journalRows = 0;

  constructor(private readonly dbName: string) {}

  async load(): Promise<Snapshot | null> {
    if (typeof indexedDB === "undefined") return null;
    const db = await this.openDb();
    try {
      const base = await request<Snapshot | undefined>(
        db.transaction(BASE, "readonly").objectStore(BASE).get(BASE_KEY),
      );
      const journal = await request<JournalRecord[]>(
        db.transaction(JOURNAL, "readonly").objectStore(JOURNAL).getAll(),
      );
      if (!base && journal.length === 0) return null;
      const snap: Snapshot = base ?? {};
      this.journalCount = journal.length;
      this.journalRows = journal.reduce((n, r) => n + r.upserts.length, 0);
      for (const record of journal) applyJournal(snap, record);
      return snap;
    } finally {
      db.close();
    }
  }

  async save(snap: Snapshot): Promise<void> {
    if (typeof indexedDB === "undefined") return;
    const db = await this.openDb();
    try {
      const tx = db.transaction([BASE, JOURNAL], "readwrite");
      tx.objectStore(BASE).put(snap, BASE_KEY);
      tx.objectStore(JOURNAL).clear();
      await done(tx);
      this.journalCount = 0;
      this.journalRows = 0;
    } finally {
      db.close();
    }
  }

  async saveDelta(snap: Snapshot, delta: PersistenceDelta): Promise<void> {
    if (typeof indexedDB === "undefined") return;
    const rows = [...delta.changes.values()].reduce((n, c) => n + c.upserts.length, 0);
    if (this.journalCount + delta.changes.size > JOURNAL_MAX || this.journalRows + rows > JOURNAL_MAX_ROWS) {
      return this.save(snap);
    }
    const db = await this.openDb();
    try {
      const tx = db.transaction(JOURNAL, "readwrite");
      const store = tx.objectStore(JOURNAL);
      for (const [table, change] of delta.changes) {
        store.put({ table, upserts: change.upserts, deletes: change.deletes, cleared: change.cleared });
      }
      await done(tx);
      this.journalCount += delta.changes.size;
      this.journalRows += rows;
    } finally {
      db.close();
    }
  }

  private openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(this.dbName, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(BASE)) db.createObjectStore(BASE);
        if (!db.objectStoreNames.contains(JOURNAL)) db.createObjectStore(JOURNAL, { autoIncrement: true });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
}

/** Replay one journal record onto the base snapshot, in place. Exported for
 *  tests: this is where a reload silently loses an edit if it is wrong. */
export function applyJournal(snap: Snapshot, record: JournalRecord): void {
  const { table, upserts, deletes, cleared } = record;
  let rows = cleared ? [] : snap[table] ?? [];
  if (deletes.length > 0) {
    const gone = new Set(deletes);
    rows = rows.filter((r) => !gone.has(String(r["id"] ?? r["key"])));
  }
  if (upserts.length > 0) {
    const at = new Map<string, number>();
    rows.forEach((r, i) => at.set(String(r["id"] ?? r["key"]), i));
    for (const row of upserts) {
      const key = String(row["id"] ?? row["key"]);
      const index = at.get(key);
      if (index === undefined) {
        at.set(key, rows.length);
        rows.push(row);
      } else {
        rows[index] = row;
      }
    }
  }
  snap[table] = rows;
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}
